import type { PlacementFormPosition } from "@design-lab/shared/types";

import { getStandardPlacementForGarmentSize } from "./garmentSizeStandard";
import { fitArtworkToStandard, type ArtworkSize } from "./fitArtworkToStandard";
import type { StandardPlacementDimensions } from "./printStandardPlacement";

/**
 * MN-8890 wiring — decide how to resize artwork for a placement given the garment sizes selected
 * in the order. Pure + deterministic so the hot-path hook stays a thin wrapper.
 *
 * Honest scope (W2 = Option B; Max comment 246936): the cart stores ONE width/height per placement
 * shared across variants today, so true per-line-item dimensions land with the MN-9933 backend.
 * Until then this resolver only auto-resizes when it is UNAMBIGUOUS — i.e. every selected size maps
 * to the SAME standard for the placement — and otherwise falls through to legacy sizing. It never
 * picks a single representative size for a genuinely mixed order (Max explicitly rejected that
 * compromise) and never guesses an unmapped size (Kevin B1b).
 */
export type ResizeResolution =
  | { kind: "resize"; size: ArtworkSize; standard: StandardPlacementDimensions }
  | { kind: "fallthrough"; reason: "no-sizes" | "unmapped" | "mixed"; unmappedSizes: string[] };

export function resolveStandardPlacementResize(params: {
  placement: PlacementFormPosition;
  selectedSizes: string[];
  currentSize: ArtworkSize;
}): ResizeResolution {
  const { placement, selectedSizes, currentSize } = params;

  if (selectedSizes.length === 0) {
    return { kind: "fallthrough", reason: "no-sizes", unmappedSizes: [] };
  }

  const unmappedSizes: string[] = [];
  const standards: StandardPlacementDimensions[] = [];

  selectedSizes.forEach(size => {
    const standard = getStandardPlacementForGarmentSize(placement, size);
    if (!standard) {
      unmappedSizes.push(size);
      return;
    }
    standards.push(standard);
  });

  // Any unmapped size → fail safe (manual + CST flag), don't resize this placement.
  if (unmappedSizes.length > 0) {
    return { kind: "fallthrough", reason: "unmapped", unmappedSizes };
  }

  // Distinct standards across the order → genuinely per-line-item; defer to the MN-9933 backend.
  const distinct = new Set(standards.map(s => `${s.widthInches}x${s.heightInches}`));
  if (distinct.size > 1) {
    return { kind: "fallthrough", reason: "mixed", unmappedSizes: [] };
  }

  // One shared standard across every selected size → safe to apply (cap, not target).
  const [standard] = standards;
  if (!standard) return { kind: "fallthrough", reason: "no-sizes", unmappedSizes: [] };
  return { kind: "resize", size: fitArtworkToStandard(currentSize, standard), standard };
}
